/**
 * Note widget factory — owns API→UI mapping, edit-callback wiring, and
 * note-specific config/state helpers. Re-entry detail files only register
 * or invoke this factory; they do not contain note implementation details.
 */

import { NOTE_WIDGET_TYPE } from '../../constants/reentry-planning.constants';

const isPlainObject = (value) =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

export const isNoteWidget = (widget) => widget?.widgetType === NOTE_WIDGET_TYPE;

/**
 * Maps API note config to Note WidgetBody props.
 * Defaults to readOnly; editable views attach callbacks via attachEditCallbacks.
 */
export const mapNoteWidgetProps = (widget, _reentryPlanItems = {}, options = {}) => {
  const config = isPlainObject(widget?.config) ? widget.config : {};
  const readOnly = options.readOnly !== false;

  return {
    body: typeof config.body === 'string' ? config.body : '',
    readOnly,
    showEditButton: false,
  };
};

/** Builds the full widget config payload for PATCH. */
export const toNoteWidgetConfig = (props = {}) => ({
  body: typeof props.body === 'string' ? props.body : '',
});

/**
 * Attaches Save callback and enables Edit for one editable note widget.
 * Handlers are keyed by widget id (API UUID) inside each callback.
 *
 * @param {object} widget - Mapped UI widget (`type` / `id` / `props`)
 * @param {object} handlers - Note mutation handlers from useNoteWidgetMutations
 */
export const attachNoteEditCallbacks = (widget, handlers) => ({
  ...widget,
  props: {
    ...widget.props,
    readOnly: false,
    showEditButton: true,
    onSave: (body) => handlers.onSave(widget.id, body),
  },
});

/** Replaces note props for one widget id across sections (local state after PATCH). */
export const replaceNoteWidgetProps = (sections = [], widgetId, nextProps) =>
  sections.map((section) => ({
    ...section,
    widgets: (section.widgets ?? []).map((widget) => {
      if (widget.id !== widgetId || widget.type !== NOTE_WIDGET_TYPE) {
        return widget;
      }
      return {
        ...widget,
        props: {
          ...widget.props,
          ...nextProps,
        },
      };
    }),
  }));

/**
 * Registry entry for section mapping and editable callback attachment.
 * Future widgets export the same shape from their own package.
 */
export const noteWidgetFactory = {
  matches: isNoteWidget,
  type: NOTE_WIDGET_TYPE,
  mapProps: mapNoteWidgetProps,
  attachEditCallbacks: attachNoteEditCallbacks,
};
